import { QUESTIONNAIRE_SCORE_CALCULATIONS } from "@repo/domain";
import { z } from "zod";
import { unitSchema } from "./common.schemas";
import { newCanonicalSchema } from "./entity-id.schemas";
import { patientFactConditionSchema } from "./patient-fact-condition.schemas";

const memberIdSchema = z.string().regex(/^[a-z][a-z0-9]*-[1-9]\d*$/);
const clientRefSchema = z.string().trim().min(1).max(200);
const memberReferenceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Persisted"), id: memberIdSchema }),
  z.object({ kind: z.literal("Temporary"), clientRef: clientRefSchema }),
]);

const choiceOptionSchema = z.object({
  detail: z.string().trim().min(1).optional(),
  id: memberIdSchema.optional(),
  clientRef: clientRefSchema.optional(),
  label: z.string().trim().min(1),
  points: z.number().int().optional(),
});

const questionnaireValueSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("Quantity"),
    unit: unitSchema.optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    step: z.number().positive().optional(),
  }),
  z.object({
    kind: z.literal("Integer"),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    step: z.number().int().positive().optional(),
  }),
  z.object({
    kind: z.literal("Choice"),
    options: z.array(choiceOptionSchema).min(1),
    cardinality: z.enum(["Single", "Multiple"]),
    allowFreeText: z.boolean(),
  }),
  z.object({ kind: z.literal("Boolean") }),
  z.object({
    kind: z.literal("Text"),
    maxLength: z.number().int().positive().optional(),
    format: z.enum(["SingleLine", "Email", "Phone"]).optional(),
  }),
  z.object({ kind: z.literal("Date") }),
  z.object({ kind: z.literal("Attachment") }),
  z.object({ kind: z.literal("Url") }),
]);

const conditionValueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
  z.object({ kind: z.literal("Integer"), value: z.number().int() }),
  z.object({ kind: z.literal("Quantity"), value: z.number().finite() }),
  z.object({ kind: z.literal("Text"), value: z.string() }),
  z.object({ kind: z.literal("Date"), value: z.string() }),
  z.object({ kind: z.literal("Choice"), option: memberReferenceSchema }),
]);

const conditionSchema = z.object({
  question: memberReferenceSchema,
  operator: z.enum(["=", "!=", ">", "<", ">=", "<=", "exists"]),
  value: conditionValueSchema,
});

const conditionalShape = {
  conditions: z.array(z.union([conditionSchema, patientFactConditionSchema])).optional(),
  conditionBehavior: z.enum(["any", "all"]).optional(),
};

const questionSchema = z.object({
  help: z.string().trim().min(1).optional(),
  id: memberIdSchema.optional(),
  clientRef: clientRefSchema.optional(),
  kind: z.literal("Question"),
  label: z.string().trim().min(1),
  required: z.boolean(),
  value: questionnaireValueSchema,
  askYesNoFirst: z.boolean().optional(),
  ...conditionalShape,
});

const displaySchema = z.object({
  id: memberIdSchema.optional(),
  clientRef: clientRefSchema.optional(),
  kind: z.literal("Display"),
  text: z.string().trim().min(1),
});

const repeatingGroupSchema = z.object({
  id: memberIdSchema.optional(),
  clientRef: clientRefSchema.optional(),
  kind: z.literal("RepeatingGroup"),
  label: z.string().trim().min(1),
  minOccurrences: z.number().int().nonnegative(),
  items: z.array(z.discriminatedUnion("kind", [questionSchema, displaySchema])).min(1),
});

const itemSchema = z.discriminatedUnion("kind", [questionSchema, displaySchema, repeatingGroupSchema]);
const sectionSchema = z.object({
  id: memberIdSchema.optional(),
  clientRef: clientRefSchema.optional(),
  label: z.string().trim().min(1),
  showTogether: z.boolean().optional(),
  items: z.array(itemSchema).min(1),
  ...conditionalShape,
});

const scoreBandSchema = z.object({
  label: z.string().trim().min(1),
  from: z.number().int(),
  to: z.number().int().optional(),
});

const questionnaireDefinitionContentSchema = z.object({
  sections: z.array(sectionSchema).min(1),
  scores: z
    .array(
      z.object({
        id: memberIdSchema.optional(),
        clientRef: clientRefSchema.optional(),
        label: z.string().trim().min(1),
        items: z.array(memberReferenceSchema).min(1),
        bands: z.array(scoreBandSchema).optional(),
        calculation: z.enum(QUESTIONNAIRE_SCORE_CALCULATIONS).optional(),
      }),
    )
    .optional(),
  /** Omitted on a revision keeps the current Total bands; an empty list clears them. */
  totalBands: z.array(scoreBandSchema).optional(),
});

export const defineQuestionnaireDefinitionInputSchema = questionnaireDefinitionContentSchema.extend({
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type DefineQuestionnaireDefinitionInput = z.infer<typeof defineQuestionnaireDefinitionInputSchema>;

export const reviseQuestionnaireDefinitionInputSchema = questionnaireDefinitionContentSchema.extend({
  definitionUrl: z.string().url(),
  expectedVersion: z.number().int().positive(),
});
export type ReviseQuestionnaireDefinitionInput = z.infer<typeof reviseQuestionnaireDefinitionInputSchema>;

export const correctQuestionnaireDefinitionInputSchema = z.object({
  definitionUrl: z.string().url(),
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectQuestionnaireDefinitionInput = z.infer<typeof correctQuestionnaireDefinitionInputSchema>;

export const changeQuestionnaireDefinitionAvailabilityInputSchema = z.object({ definitionUrl: z.string().url() });
export type ChangeQuestionnaireDefinitionAvailabilityInput = z.infer<
  typeof changeQuestionnaireDefinitionAvailabilityInputSchema
>;
